import { CodeDescriptionDto, FileMappingSuggestionDto, PreprocessingFileDto } from "api/api.typing";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InfoIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useFileMappingState, FileMapping } from "./useFileMappingState";
import { FileMappingToolbar } from "./FileMappingToolbar";
import { FileMappingGridRow } from "./FileMappingGridRow";

interface PreprocessingFileMappingTableProps {
  files: File[];
  sourceFiles: PreprocessingFileDto[];
  targetLanguages: CodeDescriptionDto[];
  fileMappingSuggestions: FileMappingSuggestionDto[];
  onValidationChange?: (isValid: boolean) => void;
  onMappingsChange?: (mappings: Map<string, FileMapping>) => void;
}

export function PreprocessingFileMappingTable({
  files,
  sourceFiles,
  targetLanguages,
  fileMappingSuggestions,
  onValidationChange,
  onMappingsChange,
}: PreprocessingFileMappingTableProps) {
  const { t: translation } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;

  const {
    selectedFiles,
    batchSourceFileIds,
    batchLanguages,
    fileMappings,
    areAllMappingsComplete,
    handleSelectAll,
    handleFileSelect,
    handleBatchSourceFilesChange,
    handleBatchLanguagesChange,
    handleSourceFileChange,
    handleLanguageChange,
    handleApplyToSelected,
  } = useFileMappingState(files, fileMappingSuggestions);

  // Notify parent when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(areAllMappingsComplete);
    }
  }, [areAllMappingsComplete, onValidationChange]);

  // Notify parent when mappings change
  useEffect(() => {
    if (onMappingsChange) {
      onMappingsChange(fileMappings);
    }
  }, [fileMappings, onMappingsChange]);

  // Filter files by search term (case-insensitive)
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) return files;

    const lowerSearch = searchTerm.toLowerCase();
    return files.filter((file) => file.name.toLowerCase().includes(lowerSearch));
  }, [files, searchTerm]);

  // Pagination calculations (use filteredFiles)
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFiles = filteredFiles.slice(startIndex, endIndex);

  // Reset to last valid page if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleSearchChange = (value: string | null) => {
    setSearchTerm(value || "");
  };

  return (
    <>
      {/*File Mapping Section Title*/}
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <h5 className="text-sm font-medium">{translation("task.upload.file-mapping-title")}</h5>
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <InfoIcon className="size-4 cursor-help text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
            {showTooltip && (
              <div className="absolute left-0 z-10 mt-2 w-96">
                <div className="rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5 dark:bg-gray-800">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {translation("task.upload.file-mapping-tooltip")}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
                    <li>{translation("task.upload.preprocessing-warning-bullet-1")}</li>
                    <li>{translation("task.upload.preprocessing-warning-bullet-2")}</li>
                    <li>{translation("task.upload.preprocessing-warning-bullet-3")}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-md bg-amber-50 p-4 dark:bg-amber-900/20">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {translation("task.upload.preprocessing-warning-title")}
        </p>
      </div>

      {/*Batch Operations Toolbar with Search*/}
      <FileMappingToolbar
        files={files}
        sourceFiles={sourceFiles}
        targetLanguages={targetLanguages}
        selectedFiles={selectedFiles}
        batchSourceFiles={batchSourceFileIds}
        batchLanguages={batchLanguages}
        onSelectAll={handleSelectAll}
        onBatchSourceFilesChange={handleBatchSourceFilesChange}
        onBatchLanguagesChange={handleBatchLanguagesChange}
        onApplyToSelected={handleApplyToSelected}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />

      {/*File Mapping Grid*/}
      <div className="mt-4">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" className="w-12 p-3"></th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                {translation("task.upload.file-mapping.uploaded-file")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                {translation("task.upload.file-mapping.source-file")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                {translation("task.upload.file-mapping.languages")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {currentFiles.length > 0 ? (
              currentFiles.map((file) => {
                const mapping = fileMappings.get(file.name) || { sourceFileIds: [], languageCodes: [] };
                return (
                  <FileMappingGridRow
                    key={file.name}
                    file={file}
                    mapping={mapping}
                    isSelected={selectedFiles.has(file.name)}
                    sourceFiles={sourceFiles}
                    targetLanguages={targetLanguages}
                    onSelect={handleFileSelect}
                    onSourceFileChange={handleSourceFileChange}
                    onLanguageChange={handleLanguageChange}
                  />
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {translation("task.upload.file-mapping.no-matches")}
                  </p>
                  <button
                    onClick={() => handleSearchChange(null)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {translation("common.clear-search")}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {translation("common.showing")} {startIndex + 1}-{Math.min(endIndex, filteredFiles.length)}{" "}
              {translation("common.of")} {filteredFiles.length}
              {searchTerm && (
                <span className="ml-1 text-gray-400">
                  ({files.length} {translation("common.total")})
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="flex items-center space-x-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="size-4" />
                <span>{translation("common.previous")}</span>
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {translation("common.page")} {currentPage} {translation("common.of")} {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <span>{translation("common.next")}</span>
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
